import { Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { trackFloatingCTAClick } from "@/lib/ga4";

interface FloatingCTAProps {
  forceShow?: boolean;
}

const FloatingCTA = ({ forceShow = false }: FloatingCTAProps) => {
  const location = useLocation();
  const { t } = useTranslation();
  const [hasScrolled, setHasScrolled] = useState(false);
  const [showCTA, setShowCTA] = useState(false);
  
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    
    // Check if already scrolled on mount
    if (window.scrollY > 0) {
      timeoutId = setTimeout(() => {
        setShowCTA(true);
      }, 1000);
      setHasScrolled(true);
      return;
    }
    
    const handleScroll = () => {
      if (!hasScrolled && window.scrollY > 0) {
        setHasScrolled(true);
        // Show CTA 1 second after first scroll
        timeoutId = setTimeout(() => {
          setShowCTA(true);
        }, 1000);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [hasScrolled]);
  
  // Hide on admin page
  if (location.pathname === '/admin') {
    return null;
  }

  // Don't show until scroll + 1s delay
  if (!showCTA && !forceShow) {
    return null;
  }

  return (
    <div className="fixed bottom-[10vh] right-2 z-40 animate-fade-in">
      <a
        href="https://bookmenow.info/book/MCAL-77c82632-3a93-4622-8307-4d804036002e/ersttermin"
        target="_blank"
        rel="noopener noreferrer"
        className="block hover-lift"
        onClick={() => trackFloatingCTAClick()}
      >
        <Button
          className="relative overflow-hidden bg-primary text-primary-foreground hover:bg-gold-dark shadow-[0_0_20px_rgba(228,197,116,0.3)] font-body font-medium rounded-full px-3 py-1.5 flex items-center gap-1.5 text-xs h-auto"
        >
          <Calendar className="w-3.5 h-3.5 relative z-10 flex-shrink-0" />
          <span className="hidden sm:inline relative z-10">{t("floating.bookCall")}</span>
          <span className="sm:hidden relative z-10">{t("floating.bookCallShort")}</span>
          <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/60 to-transparent animate-shimmer"></span>
        </Button>
      </a>
    </div>
  );
};

export default FloatingCTA;
